import React, { type ReactElement, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { Box, Tooltip } from '@mui/material'
import isEqual from 'lodash/isEqual'
import snakeCase from 'lodash/snakeCase'

import {
  Banner,
  Button,
  EmptyState,
  Icon,
  IconButton,
  Table,
  type TableHeader,
  TextField,
  Toast,
  type ToastType,
} from '~components/internal'
import { useAccess } from '~hooks/access/useAccess'
import { type RootState } from '~redux/rootReducer'
import {
  useCreateCustomCategoryEvaluationConfigMutation,
  useGetEvaluationCategoriesQuery,
  useGetInteractionCategoryUpdateStatusQuery,
  useUpdateCustomCategoryEvaluationConfigMutation,
  useUpdateInteractionCategoriesMutation,
} from '~redux/slices/apiSlice'
import { typography } from '~tokens/internal'
import { type IdentityId } from '~types'
import { toTitleCase } from '~utils/textUtils'

import TableToolbar from '../Table/TableToolbar'

import AICategoriesBackfillDialog from './AICategoriesBackfillDialog'
import './AICategoriesEditor.scss'
import AICategoriesSaveDialog from './AICategoriesSaveDialog'

export interface AICategoriesEditorProps {
  organizationIdentityId: string
}

type CategoryRow = {
  name: string
  definition: string
}

const createCategoryRow = (name = '', definition = ''): CategoryRow => ({
  name,
  definition,
})

const AICategoriesEditor: React.FC<AICategoriesEditorProps> = ({
  organizationIdentityId,
}): ReactElement => {
  const userId = useSelector((state: RootState) => state?.auth.userId)
  const { allowed: hasOrgSettingsWrite, isSuperUser } = useAccess({
    permission: { resource: 'organization_settings', action: 'write' },
    targetOrganizationIdentityId: organizationIdentityId as IdentityId,
  })
  const canBackfill = isSuperUser && hasOrgSettingsWrite
  const {
    currentData: data,
    isError,
    isLoading,
    isFetching,
  } = useGetEvaluationCategoriesQuery(
    {
      user_id: userId ?? '',
      organization_identity_id: organizationIdentityId,
    },
    { refetchOnMountOrArgChange: true, skip: !userId },
  )
  const [createCategories, { isLoading: isCreating }] =
    useCreateCustomCategoryEvaluationConfigMutation()
  const [updateCategories, { isLoading: isUpdating }] =
    useUpdateCustomCategoryEvaluationConfigMutation()
  const [rows, setRows] = useState<CategoryRow[]>([])
  const [baselineRows, setBaselineRows] = useState<CategoryRow[]>([])
  const [showSavedBanner, setShowSavedBanner] = useState(false)
  const [showSaveDialog, setShowSaveDialog] = useState(false)
  const [showBackfillDialog, setShowBackfillDialog] = useState(false)
  const [savedConfig, setSavedConfig] = useState(false)
  const [toast, setToast] = useState<{ type: ToastType; message: string } | null>(null)

  const hasExistingConfig = savedConfig || data?.is_custom === true
  const [backfillPollingInterval, setBackfillPollingInterval] = useState(0) // ms
  const { data: backfillStatus, refetch: refetchBackfillStatus } =
    useGetInteractionCategoryUpdateStatusQuery(
      {
        user_id: userId ?? '',
        organization_identity_id: organizationIdentityId,
      },
      {
        skip: !userId || !canBackfill || !hasExistingConfig,
        refetchOnMountOrArgChange: true,
        pollingInterval: backfillPollingInterval,
      },
    )
  const [startBackfillMutation, { isLoading: startingBackfill }] =
    useUpdateInteractionCategoriesMutation()
  const backfilling = backfillStatus?.in_progress === true
  const saving = isCreating || isUpdating
  const loading = isLoading || isFetching || !userId

  // Clear save state values when org changes
  useEffect(() => {
    setSavedConfig(false)
    setShowSavedBanner(false)
    setToast(null)
    setRows([])
    setBaselineRows([])
  }, [organizationIdentityId])

  useEffect(() => {
    if (!data) return
    const seededRows = data.categories.map((category) =>
      createCategoryRow(toTitleCase(category.name), category.definition ?? ''),
    )
    setRows(seededRows)
    setBaselineRows(seededRows)
  }, [data])

  useEffect(() => {
    if (backfillPollingInterval > 0 && !backfilling) {
      setToast({ type: 'success', message: 'Backfill complete' })
    }
    setBackfillPollingInterval(backfilling ? 5000 : 0)
  }, [backfilling, backfillPollingInterval])

  useEffect(() => {
    if (isError) {
      setToast({ type: 'warning', message: 'Failed to load categories' })
    }
  }, [isError])

  const updateRow = (index: number, updates: Partial<CategoryRow>): void => {
    setShowSavedBanner(false)
    setRows((currentRows) => {
      const nextRows = [...currentRows]
      nextRows[index] = { ...nextRows[index], ...updates }
      return nextRows
    })
  }

  const handleAddCategory = (): void => {
    setShowSavedBanner(false)
    setRows((currentRows) => [...currentRows, createCategoryRow()])
  }

  const handleRemoveCategory = (index: number): void => {
    setShowSavedBanner(false)
    setRows((currentRows) => currentRows.filter((_, rowIndex) => rowIndex !== index))
  }

  const namedRows = rows.filter((row) => snakeCase(row.name) !== '')
  const categories = namedRows.map((row) => {
    const name = snakeCase(row.name)
    const definition = row.definition.replace(/\s*\n+\s*/g, ' ').trim()
    return definition ? { name, definition } : { name }
  })

  const handleSave = async (): Promise<void> => {
    if (!organizationIdentityId || !userId || namedRows.length === 0) return
    try {
      const request = {
        user_id: userId,
        organization_identity_id: organizationIdentityId,
        categories,
      }
      if (hasExistingConfig) {
        await updateCategories(request).unwrap()
      } else {
        await createCategories(request).unwrap()
      }
      setBaselineRows(rows)
      setSavedConfig(true)
      setShowSavedBanner(true)
    } catch {
      setToast({ type: 'warning', message: 'Failed to save categories' })
    }
  }

  const handleConfirmSave = async (): Promise<void> => {
    try {
      await handleSave()
    } finally {
      setShowSaveDialog(false)
    }
  }

  const isDirty = !isEqual(rows, baselineRows)

  const categoryHeaders: TableHeader<CategoryRow & { id: number }>[] = [
    {
      id: 'name',
      label: 'Category',
      render: (row, rowIndex) => (
        <TextField
          size='small'
          containerStyle={{ width: '100%' }}
          placeholder='e.g. Service Request'
          value={row.name}
          onChange={(event) => updateRow(rowIndex, { name: event.target.value })}
        />
      ),
    },
    {
      id: 'definition',
      label: 'Definition (optional)',
      render: (row, rowIndex) => (
        <TextField
          size='small'
          multiline
          minRows={1}
          containerStyle={{ width: '100%' }}
          placeholder='Add an explanation for commonly confused categories.'
          value={row.definition}
          onChange={(event) => updateRow(rowIndex, { definition: event.target.value })}
        />
      ),
    },
    {
      id: 'actions',
      label: '',
      align: 'right',
      render: (_, rowIndex) => (
        <Tooltip title='Remove category'>
          <IconButton aria-label='Remove category' onClick={() => handleRemoveCategory(rowIndex)}>
            <Icon name='delete' size={typography.fontSizePx.md} />
          </IconButton>
        </Tooltip>
      ),
    },
  ]

  const startBackfill = async (startDate: string, endDate: string): Promise<void> => {
    setShowBackfillDialog(false)
    if (!userId) return
    const result = await startBackfillMutation({
      user_id: userId,
      organization_identity_id: organizationIdentityId,
      start_date: startDate,
      end_date: endDate,
    })
    if (result.data) {
      setToast({
        type: 'success',
        message: `Backfill started for ${result.data.interaction_count.toLocaleString()} interactions`,
      })
      refetchBackfillStatus()
    } else {
      setToast({ type: 'warning', message: 'Failed to start the backfill' })
    }
  }

  const actions = (
    <>
      {canBackfill && hasExistingConfig && (
        <Button
          variant='outlined'
          onClick={() => setShowBackfillDialog(true)}
          loading={startingBackfill || backfilling}
        >
          {startingBackfill || backfilling ? 'Backfilling…' : 'Backfill categories'}
        </Button>
      )}
      <Button variant='outlined' startIcon={<Icon name='add' />} onClick={handleAddCategory}>
        Add category
      </Button>
      <Button
        variant='contained'
        color='primary'
        onClick={() => setShowSaveDialog(true)}
        disabled={loading || saving || namedRows.length === 0 || !isDirty}
      >
        Save categories
      </Button>
    </>
  )

  return (
    <Box className='ai-categories-editor'>
      {!loading && !data?.is_custom && !savedConfig && (
        <Banner
          type='info'
          title='Showing the default categories'
          body="Your organization hasn't customized its AI interaction categories yet. Edit and save to create a custom set."
        />
      )}
      {showSavedBanner && (
        <Banner
          type='success'
          title='Categories saved'
          body={`New interactions will be evaluated using these ${namedRows.length} categories.`}
        />
      )}
      <Table<CategoryRow & { id: number }>
        headers={categoryHeaders}
        data={rows.map((row, index) => ({ ...row, id: index }))}
        divider
        loading={loading}
        toolbar={<TableToolbar actions={actions} />}
        emptyMessage={
          <EmptyState
            variant='micro'
            title='No categories yet'
            description='Add a first category to start replacing the default set.'
          />
        }
      />
      <AICategoriesBackfillDialog
        open={showBackfillDialog}
        onClose={() => setShowBackfillDialog(false)}
        onStart={startBackfill}
      />
      <Toast
        type={toast?.type ?? 'success'}
        message={toast?.message ?? ''}
        open={toast !== null}
        onClose={() => setToast(null)}
        autoHideDuration={6000}
      />
      <AICategoriesSaveDialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        organizationIdentityId={organizationIdentityId}
        categories={categories}
        saving={saving}
        canBackfill={canBackfill}
        onSave={handleConfirmSave}
      />
    </Box>
  )
}

export default AICategoriesEditor
